'use client';
import { GraphQLError } from 'graphql';
import { CurrentUserFields } from '@generated/graphql';
import { getOrCreateAnonymousId } from './anonymousId';
import { isApolloError, reportAPIError, reportApolloError, reportGraphQLError } from './errorUtils';
import { captureError, setUser as setSentryUser } from './sentry';

export function setErrorReportingUser(user?: CurrentUserFields): void {
  if (user) {
    setSentryUser({
      id: user._id,
      email: user.privateFields.email ?? undefined,
      username: user.nickname,
    });
    return;
  }

  // Logged-out visitors still get a stable synthetic id so Sentry counts them
  // as unique users.
  const anonymousId = getOrCreateAnonymousId();
  setSentryUser(anonymousId ? { id: anonymousId } : undefined);
}

/*
 * This function is used to report errors that occur on the client.
 * Do not use this function to report errors in server components.
 * Use `reportMykissServerError` instead.
 */
export function reportMykissClientError(
  error?: unknown,
  context?: string,
  severity?: 'info' | 'warning',
  otherData?: object,
): void {
  if (!error) {
    return;
  }

  // The below check of error.text is a replacement for `error instanceof Response`, as the Response
  // class is not available in the Node environment. A Fetch API response should have a `text`
  // function (we use it in `reportAPIError`), so we use it's existance to decide the error is from
  // a Fetch request.
  // @ts-ignore
  if (error.text) {
    reportAPIError(error as Response, severity, otherData, context);
  } else if (isApolloError(error)) {
    // Apollo 4: All Apollo error types (GraphQL errors, network errors, parsing errors)
    reportApolloError(error, severity, otherData, context);
  } else if (error instanceof GraphQLError) {
    reportGraphQLError(error, severity, otherData, context);
  } else if (error instanceof Error) {
    captureError(error, {
      context,
      severity: error.name === 'NotFoundError' ? (severity ?? 'info') : (severity ?? 'error'),
      metadata: otherData as Record<string, unknown>,
    });
  } else {
    // For non-Error values, convert to Error
    const errorMessage = typeof error === 'string' ? error : JSON.stringify(error);
    captureError(new Error(errorMessage), {
      context,
      severity: severity ?? 'error',
      metadata: { originalError: error, ...(otherData as Record<string, unknown>) },
    });
  }
}
